import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import managedAxios from '@/utils/managedAxios';
import { API_BASE } from '@/App';
import { useToast } from '@/hooks/use-toast';
import HandwrittenUpload from '@/components/HandwrittenUpload';
import {
  ArrowLeft, PenTool, Target, BarChart3, FileText,
  Loader2, CheckCircle, XCircle, Brain, Crown, Lock,
  Lightbulb, Flame, Snowflake, Thermometer, AlertTriangle,
  CheckCircle2, Circle, TrendingUp, Award, Sparkles, AlertCircle, Search, Pencil, CircleCheck, ArrowRight,
  Camera, FileText as FileTextIcon
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// DIAGRAM COMPONENTS (Similar to GS Suite)
// ═══════════════════════════════════════════════════════════════

// Flowchart Diagram Component
const FlowchartDiagram = ({ diagram }) => {
  const elements = Array.isArray(diagram.elements) ? diagram.elements : ['Step 1', 'Step 2', 'Step 3'];
  const colors = [
    'bg-indigo-100 border-indigo-400 text-indigo-800',
    'bg-purple-100 border-purple-400 text-purple-800',
    'bg-blue-100 border-blue-400 text-blue-800',
    'bg-cyan-100 border-cyan-400 text-cyan-800',
    'bg-teal-100 border-teal-400 text-teal-800'
  ];

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 py-4">
      {elements.map((element, idx) => (
        <React.Fragment key={idx}>
          <div className={`border-2 rounded-lg px-4 py-3 text-center min-w-[100px] ${colors[idx % colors.length]}`}>
            <p className="text-sm font-semibold">{element}</p>
          </div>
          {idx < elements.length - 1 && (
            <div className="text-indigo-500 text-2xl font-bold">→</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Hub & Spoke Diagram Component
const HubSpokeDiagram = ({ diagram }) => {
  const center = diagram.title || 'Central Concept';
  const spokes = Array.isArray(diagram.elements) ? diagram.elements : ['Element 1', 'Element 2', 'Element 3', 'Element 4'];
  const colors = [
    'bg-red-100 border-red-400',
    'bg-orange-100 border-orange-400',
    'bg-amber-100 border-amber-400',
    'bg-emerald-100 border-emerald-400',
    'bg-blue-100 border-blue-400',
    'bg-purple-100 border-purple-400'
  ];

  return (
    <div className="relative flex items-center justify-center py-8" style={{ minHeight: '250px' }}>
      {/* Center Hub */}
      <div className="absolute z-10 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
        <p className="text-[10px] font-bold text-center px-2 leading-tight">{center}</p>
      </div>
      
      {/* Spokes */}
      {spokes.slice(0, 6).map((spoke, idx) => {
        const angle = (idx * 360) / Math.min(spokes.length, 6) - 90;
        const rad = (angle * Math.PI) / 180;
        const x = Math.cos(rad) * 90;
        const y = Math.sin(rad) * 90;
        
        return (
          <div
            key={idx}
            className={`absolute border-2 rounded-lg px-3 py-2 shadow-sm ${colors[idx % colors.length]}`}
            style={{
              left: `calc(50% + ${x}px - 50px)`,
              top: `calc(50% + ${y}px - 20px)`,
              width: '100px'
            }}
          >
            <p className="text-[10px] font-semibold text-center">{spoke}</p>
          </div>
        );
      })}
    </div>
  );
};

// Timeline Diagram Component
const TimelineDiagram = ({ diagram }) => {
  const elements = Array.isArray(diagram.elements) ? diagram.elements : ['Stage 1', 'Stage 2', 'Stage 3'];
  const colors = [
    'bg-amber-500',
    'bg-orange-500',
    'bg-red-500',
    'bg-rose-500',
    'bg-pink-500'
  ];

  return (
    <div className="py-4">
      <div className="relative flex items-center justify-between">
        {/* Timeline line */}
        <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 top-1/2 transform -translate-y-1/2 z-0" />
        
        {elements.map((element, idx) => (
          <div key={idx} className="relative z-10 flex flex-col items-center" style={{ flex: 1 }}>
            <div className={`w-8 h-8 rounded-full ${colors[idx % colors.length]} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
              {idx + 1}
            </div>
            <p className="text-xs font-semibold mt-2 text-center max-w-[80px]">{element}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Classification/Hierarchy Diagram Component
const ClassificationDiagram = ({ diagram }) => {
  const title = diagram.title || 'Classification';
  const elements = Array.isArray(diagram.elements) ? diagram.elements : ['Type A', 'Type B', 'Type C'];

  return (
    <div className="py-4">
      {/* Top box */}
      <div className="flex justify-center mb-4">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg px-6 py-3 shadow-lg">
          <p className="font-bold text-sm">{title}</p>
        </div>
      </div>
      
      {/* Connecting lines */}
      <div className="flex justify-center mb-2">
        <div className="w-0.5 h-4 bg-indigo-400" />
      </div>
      <div className="flex justify-center mb-2">
        <div className="h-0.5 bg-indigo-400" style={{ width: `${Math.min(elements.length * 100, 300)}px` }} />
      </div>
      
      {/* Child boxes */}
      <div className="flex justify-center gap-4 flex-wrap">
        {elements.map((element, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <div className="w-0.5 h-4 bg-indigo-400" />
            <div className="bg-indigo-100 border-2 border-indigo-300 rounded-lg px-4 py-2 text-center min-w-[80px]">
              <p className="text-xs font-semibold text-indigo-800">{element}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Comparison Table Diagram Component - ENHANCED
const ComparisonDiagram = ({ diagram }) => {
  const elements = Array.isArray(diagram.elements) ? diagram.elements : ['Aspect 1', 'Aspect 2'];
  const labels = Array.isArray(diagram.labels) ? diagram.labels : ['Feature A', 'Feature B', 'Feature C'];
  const cellData = diagram.cell_data || {};

  return (
    <div className="overflow-x-auto py-4">
      <table className="min-w-full border-2 border-indigo-200 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
            <th className="px-4 py-3 text-left text-sm font-bold">Aspect</th>
            {elements.map((el, idx) => (
              <th key={idx} className="px-4 py-3 text-center text-sm font-bold border-l border-indigo-400">{el}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((label, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-indigo-50' : 'bg-white'}>
              <td className="px-4 py-3 text-sm font-semibold text-indigo-800 border-r-2 border-indigo-200 bg-indigo-100">{label}</td>
              {elements.map((el, elIdx) => {
                const cellValue = cellData[el]?.[idx] || cellData[label]?.[elIdx] || '—';
                return (
                  <td key={elIdx} className="px-4 py-3 text-center text-sm text-slate-700 border-l border-indigo-100">
                    {typeof cellValue === 'string' ? cellValue : (
                      <div className="flex flex-col gap-1">
                        {Array.isArray(cellValue) ? cellValue.map((v, i) => (
                          <span key={i} className="text-xs">{v}</span>
                        )) : cellValue}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Enhanced Cycle Diagram Component
const EnhancedCycleDiagram = ({ diagram }) => {
  const elements = Array.isArray(diagram.elements) ? diagram.elements : ['Step 1', 'Step 2', 'Step 3', 'Step 4'];
  const connections = Array.isArray(diagram.connections) ? diagram.connections : [];
  const cellData = diagram.cell_data || {};

  return (
    <div className="py-4">
      {/* Cycle Visualization */}
      <div className="flex flex-wrap justify-center gap-4 mb-6">
        {elements.map((el, idx) => (
          <div key={idx} className="flex items-center">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl p-4 shadow-lg min-w-[140px]">
              <div className="text-center">
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full mb-2 inline-block">{idx + 1}</span>
                <p className="font-bold text-sm">{el}</p>
                {cellData[el] && (
                  <div className="mt-2 text-xs space-y-1 opacity-90">
                    {(Array.isArray(cellData[el]) ? cellData[el].slice(0, 2) : [cellData[el]]).map((item, i) => (
                      <p key={i} className="bg-white/10 rounded px-2 py-0.5">{item}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {idx < elements.length - 1 && (
              <div className="flex items-center px-2">
                <ArrowRight className="w-6 h-6 text-indigo-400" />
              </div>
            )}
          </div>
        ))}
        {/* Return arrow */}
        <div className="w-full flex justify-center mt-2">
          <div className="flex items-center text-indigo-400 text-xs">
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span className="mx-2">Cycle repeats</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Connections Detail */}
      {connections.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-4 mt-4">
          <h5 className="font-semibold text-slate-700 mb-2 text-sm">🔗 Connections Explained:</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {connections.map((conn, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                <span className="text-indigo-500 font-bold">{idx + 1}.</span>
                <span>{conn}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cell Data Details Table */}
      {Object.keys(cellData).length > 0 && (
        <div className="mt-4">
          <h5 className="font-semibold text-slate-700 mb-2 text-sm">📋 Detailed Breakdown:</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {elements.map((el, idx) => cellData[el] && (
              <div key={idx} className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
                <h6 className="font-bold text-indigo-800 text-sm mb-2 flex items-center gap-1">
                  <span className="w-5 h-5 bg-indigo-600 text-white rounded-full text-xs flex items-center justify-center">{idx + 1}</span>
                  {el}
                </h6>
                <ul className="space-y-1">
                  {(Array.isArray(cellData[el]) ? cellData[el] : [cellData[el]]).map((item, i) => (
                    <li key={i} className="text-xs text-slate-700 flex items-start gap-1">
                      <span className="text-indigo-500 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Diagram Renderer
const DiagramRenderer = ({ diagram }) => {
  if (!diagram) return null;

  const necessityColors = {
    'MANDATORY': 'bg-green-100 border-green-500 text-green-800',
    'RECOMMENDED': 'bg-amber-100 border-amber-500 text-amber-800',
    'OPTIONAL': 'bg-slate-100 border-slate-400 text-slate-700'
  };

  const necessityIcons = {
    'MANDATORY': <CheckCircle2 className="w-4 h-4" />,
    'RECOMMENDED': <AlertTriangle className="w-4 h-4" />,
    'OPTIONAL': <Circle className="w-4 h-4" />
  };

  const necessity = diagram.necessity || 'RECOMMENDED';
  const type = diagram.type?.toLowerCase() || 'flowchart';

  return (
    <div className={`border-2 rounded-xl p-6 ${necessityColors[necessity]} mb-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">📊</span>
          <h4 className="font-bold text-lg">{diagram.title || 'Diagram'}</h4>
        </div>
        <Badge className={`${
          necessity === 'MANDATORY' ? 'bg-green-600' :
          necessity === 'RECOMMENDED' ? 'bg-amber-600' :
          'bg-slate-600'
        } text-white flex items-center gap-1`}>
          {necessityIcons[necessity]}
          {necessity === 'MANDATORY' ? '🟢 MANDATORY' :
           necessity === 'RECOMMENDED' ? '🟡 RECOMMENDED' : '⚪ OPTIONAL'}
        </Badge>
      </div>

      {/* Necessity Reason */}
      {diagram.necessity_reason && (
        <p className="text-sm italic mb-4 opacity-80">{diagram.necessity_reason}</p>
      )}

      {/* Diagram Visual */}
      <div className="bg-white rounded-lg p-4 mb-4">
        {type === 'flowchart' && <FlowchartDiagram diagram={diagram} />}
        {type === 'hub_spoke' && <HubSpokeDiagram diagram={diagram} />}
        {type === 'timeline' && <TimelineDiagram diagram={diagram} />}
        {type === 'classification' && <ClassificationDiagram diagram={diagram} />}
        {type === 'comparison_table' && <ComparisonDiagram diagram={diagram} />}
        {type === 'cycle' && <EnhancedCycleDiagram diagram={diagram} />}
        {type === 'lineage' && <TimelineDiagram diagram={diagram} />}
        {!['flowchart', 'hub_spoke', 'timeline', 'classification', 'comparison_table', 'lineage', 'cycle'].includes(type) && (
          <FlowchartDiagram diagram={diagram} />
        )}
      </div>

      {/* Labels */}
      {diagram.labels && Array.isArray(diagram.labels) && diagram.labels.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold mb-2">Key Labels:</p>
          <div className="flex flex-wrap gap-2">
            {diagram.labels.map((label, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">{label}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const Paper2Page = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const defaultTab = searchParams.get('tab') || 'generate';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Access Control State
  const [accessChecked, setAccessChecked] = useState(false);
  
  // ENGINE-1: Answer Generation
  const [answerForm, setAnswerForm] = useState({ question: '', marks: 15 });
  const [generatedAnswer, setGeneratedAnswer] = useState(null);
  
  // ENGINE-2: Evaluation
  const [evalForm, setEvalForm] = useState({ question: '', answer: '', marks: 15 });
  const [evaluation, setEvaluation] = useState(null);
  const [inputMode, setInputMode] = useState('type'); // 'type' or 'handwritten'
  
  // ENGINE-3: PYQ Analysis
  const [pyqTopic, setPyqTopic] = useState('');
  const [pyqAnalysis, setPyqAnalysis] = useState(null);
  
  // ENGINE-4: Expected Questions
  const [expectedQuestions, setExpectedQuestions] = useState(null);

  // Check access on mount
  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.get(
        `${API_BASE}/anthropology/paper2/config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If locked, redirect to optional hub for purchase
      if (response.data?.locked) {
        toast({
          title: "Purchase Required",
          description: "Please purchase the Anthropology optional to access this suite.",
          variant: "destructive"
        });
        navigate('/optional');
        return;
      }
      
      loadStats();
    } catch (err) {
      console.error('Access check error:', err);
      // If 403/locked, redirect to optional hub
      if (err.response?.status === 403 || err.response?.data?.detail?.locked) {
        toast({
          title: "Purchase Required", 
          description: "Please purchase the Anthropology optional to access this suite.",
          variant: "destructive"
        });
        navigate('/optional');
        return;
      }
    } finally {
      setAccessChecked(true);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.get(
        `${API_BASE}/anthropology/paper2/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  // Show loading while checking access
  if (!accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // ENGINE-1: Generate Answer
  const generateAnswer = async () => {
    if (!answerForm.question.trim()) {
      toast({ variant: 'destructive', description: 'Please enter a question' });
      return;
    }
    setLoading(true);
    setGeneratedAnswer(null);
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.post(
        `${API_BASE}/anthropology/paper2/generate`,
        answerForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedAnswer(response.data);
      toast({ description: '✅ Answer generated by ENGINE-1!' });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '';
      if (errorMsg.includes('Budget has been exceeded')) {
        toast({ 
          variant: 'destructive', 
          title: 'LLM Budget Exceeded',
          description: 'Please add balance to your Universal Key in Profile → Universal Key → Add Balance' 
        });
      } else {
        toast({ variant: 'destructive', description: 'Failed to generate answer. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // ENGINE-2: Evaluate Answer
  const evaluateAnswer = async () => {
    if (!evalForm.question.trim() || !evalForm.answer.trim()) {
      toast({ variant: 'destructive', description: 'Please enter question and answer' });
      return;
    }
    setLoading(true);
    setEvaluation(null);
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.post(
        `${API_BASE}/anthropology/paper2/evaluate`,
        evalForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvaluation(response.data);
      toast({ description: '✅ Evaluated by ENGINE-2!' });
      loadStats();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '';
      if (errorMsg.includes('Budget has been exceeded')) {
        toast({ 
          variant: 'destructive', 
          title: 'LLM Budget Exceeded',
          description: 'Please add balance to your Universal Key in Profile → Universal Key → Add Balance' 
        });
      } else {
        toast({ variant: 'destructive', description: 'Evaluation failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // ENGINE-3: PYQ Analysis
  const analyzePYQ = async () => {
    if (!pyqTopic.trim()) {
      toast({ variant: 'destructive', description: 'Please enter a topic' });
      return;
    }
    setLoading(true);
    setPyqAnalysis(null);
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.post(
        `${API_BASE}/anthropology/paper2/pyq-analysis`,
        { topic: pyqTopic },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPyqAnalysis(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '';
      if (errorMsg.includes('Budget has been exceeded')) {
        toast({ 
          variant: 'destructive', 
          title: 'LLM Budget Exceeded',
          description: 'Please add balance to your Universal Key in Profile → Universal Key → Add Balance' 
        });
      } else {
        toast({ variant: 'destructive', description: 'Analysis failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // ENGINE-4: Expected Questions
  const loadExpectedQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await managedAxios.get(
        `${API_BASE}/anthropology/paper2/expected-questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExpectedQuestions(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '';
      if (errorMsg.includes('Budget has been exceeded')) {
        toast({ 
          variant: 'destructive', 
          title: 'LLM Budget Exceeded',
          description: 'Please add balance to your Universal Key in Profile → Universal Key → Add Balance' 
        });
      } else {
        toast({ variant: 'destructive', description: 'Failed to load questions. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Render ENGINE-1 Answer with Visual Structure
  const renderAnswer = (data) => {
    if (!data) return null;
    
    return (
      <div className="space-y-4">
        {/* Engine Info Badges */}
        {data.syllabus_unit && (
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="outline" className="text-slate-600">📚 {data.syllabus_unit}</Badge>
          </div>
        )}

        {/* Structured Answer Card */}
        <Card className="bg-white border-2 border-indigo-100 shadow-lg">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-xl text-indigo-900">📝 Generated Answer</h4>
              <Badge className="bg-indigo-600 text-white">TOP RANKER FORMAT</Badge>
            </div>

            {/* Introduction */}
            {data.answer?.introduction && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                <h5 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
                  Introduction
                </h5>
                <p className="text-slate-700 text-sm leading-relaxed">{data.answer.introduction}</p>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* 📝 BODY SECTIONS - GS Style Enhanced */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {data.answer?.body && data.answer.body.length > 0 && (
              <Card className="border-purple-200 bg-purple-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-bold text-purple-900 uppercase">Body</h3>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">{data.answer.body.length} Key Points</Badge>
                  </div>
                  <div className="space-y-4">
                    {data.answer.body.map((section, idx) => (
                      <div key={idx} className="bg-white rounded-xl border-2 border-purple-100 p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-lg w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                            {String.fromCharCode(65 + idx)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-purple-900 text-base">{section.heading}</h4>
                            {section.thinker && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">
                                👤 {section.thinker}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="pl-11">
                          <p className="text-slate-700 text-sm leading-relaxed">{section.content}</p>
                          {section.example && (
                            <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-3">
                              <p className="text-xs font-semibold text-blue-900 mb-1">📌 Example</p>
                              <p className="text-xs text-blue-800">{section.example}</p>
                            </div>
                          )}
                          {section.significance && (
                            <div className="mt-2 bg-green-50 border-l-4 border-green-400 rounded-r-lg p-3">
                              <p className="text-xs font-semibold text-green-900 mb-1">✨ Significance</p>
                              <p className="text-xs text-green-800">{section.significance}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* DIAGRAM - Always Rendered */}
            {data.answer?.diagram && (
              <DiagramRenderer diagram={data.answer.diagram} />
            )}

            {/* Critical Analysis */}
            {data.answer?.analysis && data.answer.analysis.content && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <h5 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                  <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">⚡</span>
                  {data.answer.analysis.heading || 'Critical Analysis'}
                </h5>
                <p className="text-amber-900 text-sm leading-relaxed">{data.answer.analysis.content}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Way Forward - ENHANCED STRUCTURED VERSION */}
        {data.answer?.way_forward && (
          <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                <h3 className="text-lg font-bold text-amber-900 uppercase">Way Forward</h3>
                <Badge className="bg-amber-100 text-amber-800 text-xs">Policy Recommendations</Badge>
              </div>
              
              {/* Check if it's the new structured format or old array format */}
              {Array.isArray(data.answer.way_forward) ? (
                /* Old format - simple array */
                <div className="bg-white border-2 border-yellow-300 rounded-xl p-5">
                  <ol className="space-y-3">
                    {data.answer.way_forward.map((item, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-slate-800">{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                /* New structured format */
                <div className="space-y-4">
                  {/* Immediate Actions */}
                  {data.answer.way_forward.immediate_actions && data.answer.way_forward.immediate_actions.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🚨</span>
                        <h4 className="font-bold text-red-800">Immediate Actions</h4>
                        <Badge className="bg-red-100 text-red-700 text-xs">Short-term</Badge>
                      </div>
                      <div className="space-y-3">
                        {data.answer.way_forward.immediate_actions.map((item, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border border-red-100">
                            <div className="flex items-start gap-2">
                              <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-800 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-semibold text-slate-900 text-sm">{item.action}</p>
                                {item.why && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-red-700">Why: </span>{item.why}
                                  </p>
                                )}
                                {item.how && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-green-700">How: </span>{item.how}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Medium Term */}
                  {data.answer.way_forward.medium_term && data.answer.way_forward.medium_term.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">📋</span>
                        <h4 className="font-bold text-amber-800">Medium-Term Goals</h4>
                        <Badge className="bg-amber-100 text-amber-700 text-xs">1-3 Years</Badge>
                      </div>
                      <div className="space-y-3">
                        {data.answer.way_forward.medium_term.map((item, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border border-amber-100">
                            <div className="flex items-start gap-2">
                              <span className="flex-shrink-0 w-6 h-6 bg-amber-100 text-amber-800 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-semibold text-slate-900 text-sm">{item.action}</p>
                                {item.why && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-amber-700">Why: </span>{item.why}
                                  </p>
                                )}
                                {item.how && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-green-700">How: </span>{item.how}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Long Term */}
                  {data.answer.way_forward.long_term && data.answer.way_forward.long_term.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🎯</span>
                        <h4 className="font-bold text-blue-800">Long-Term Vision</h4>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">3+ Years</Badge>
                      </div>
                      <div className="space-y-3">
                        {data.answer.way_forward.long_term.map((item, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border border-blue-100">
                            <div className="flex items-start gap-2">
                              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-semibold text-slate-900 text-sm">{item.action}</p>
                                {item.why && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-blue-700">Why: </span>{item.why}
                                  </p>
                                )}
                                {item.how && (
                                  <p className="text-xs text-slate-600 mt-1">
                                    <span className="font-medium text-green-700">How: </span>{item.how}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Key Stakeholders & Success Indicators */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.answer.way_forward.key_stakeholders && data.answer.way_forward.key_stakeholders.length > 0 && (
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">👥</span>
                          <h4 className="font-bold text-purple-800 text-sm">Key Stakeholders</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {data.answer.way_forward.key_stakeholders.map((stakeholder, idx) => (
                            <Badge key={idx} className="bg-purple-100 text-purple-800 text-xs">
                              {stakeholder}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {data.answer.way_forward.success_indicators && data.answer.way_forward.success_indicators.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">📊</span>
                          <h4 className="font-bold text-green-800 text-sm">Success Indicators</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {data.answer.way_forward.success_indicators.map((indicator, idx) => (
                            <Badge key={idx} className="bg-green-100 text-green-800 text-xs">
                              {indicator}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Conclusion - AFTER Way Forward (like GS Suite) */}
        {data.answer?.conclusion && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                <h3 className="text-lg font-bold text-purple-900 uppercase">Conclusion</h3>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm text-slate-800 leading-relaxed">{data.answer.conclusion}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alternative Introductions */}
        {data.answer?.alternates?.intros && data.answer.alternates.intros.length > 0 && (
          <Card className="border-indigo-200 bg-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">6</div>
                <h3 className="text-lg font-bold text-indigo-900 uppercase">Alternative Introductions</h3>
              </div>
              <div className="space-y-3">
                {data.answer.alternates.intros.map((intro, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-indigo-200">
                    <p className="text-xs font-semibold text-indigo-700 mb-2">Option {idx + 1}</p>
                    <p className="text-sm text-slate-800">{intro}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alternative Conclusions */}
        {data.answer?.alternates?.conclusions && data.answer.alternates.conclusions.length > 0 && (
          <Card className="border-fuchsia-200 bg-fuchsia-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-fuchsia-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">7</div>
                <h3 className="text-lg font-bold text-fuchsia-900 uppercase">Alternative Conclusions</h3>
              </div>
              <div className="space-y-3">
                {data.answer.alternates.conclusions.map((conclusion, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 border border-fuchsia-200">
                    <p className="text-xs font-semibold text-fuchsia-700 mb-2">Option {idx + 1}</p>
                    <p className="text-sm text-slate-800">{conclusion}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topper Notes */}
        {data.answer?.topper_notes?.bullets && data.answer.topper_notes.bullets.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">8</div>
                <h3 className="text-lg font-bold text-amber-900 uppercase">Topper Notes</h3>
              </div>
              <div className="bg-white rounded-xl border-2 border-amber-200 p-5">
                <ul className="space-y-2">
                  {data.answer.topper_notes.bullets.map((note, idx) => (
                    <li key={idx} className="flex gap-3 items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-amber-100 text-amber-900 rounded-full flex items-center justify-center text-xs font-bold">★</span>
                      <span className="text-sm text-slate-800">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Checklist */}
        {data.answer?.checklist && data.answer.checklist.length > 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">9</div>
                <h3 className="text-lg font-bold text-green-900 uppercase">
                  Checklist (Must Include for {data.marks}+ Marks)
                </h3>
              </div>
              <div className="space-y-2">
                {data.answer.checklist.map((item, idx) => (
                  <div key={idx} className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
                    {item.present ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.present ? 'text-slate-800' : 'text-slate-500'}`}>{item.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Score Estimate */}
        {data.answer?.score_estimate !== undefined && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                    <Target className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-blue-900">Score Estimate</h3>
                    <p className="text-xs text-blue-600">Based on UPSC Anthropology standards</p>
                  </div>
                </div>
                <div className="text-center bg-white rounded-xl px-4 py-2 border-2 border-blue-200">
                  <p className="text-3xl font-bold text-blue-900">{data.answer.score_estimate}/10</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upgrade Message */}
        {data.upgrade_message && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <p className="text-amber-800">{data.upgrade_message}</p>
              <Button onClick={() => navigate('/optional')} className="bg-amber-600 hover:bg-amber-700">
                <Crown className="w-4 h-4 mr-2" /> Upgrade
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render ENGINE-2 Evaluation (EXACT GS Suite Style)
  const renderEvaluation = (data) => {
    if (!data) return null;

    if (data.locked) {
      return (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6 text-center">
            <Lock className="w-12 h-12 text-amber-600 mx-auto mb-3" />
            <h4 className="font-bold text-amber-900 mb-2">{data.message}</h4>
            <Button onClick={() => navigate('/optional')} className="bg-amber-600 hover:bg-amber-700">
              <Crown className="w-4 h-4 mr-2" /> Purchase Optional
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardContent className="p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
            <div className="bg-gradient-to-br from-orange-100 to-red-100 p-2 rounded-lg">
              <Award className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Anthropology Paper-II Evaluation</h2>
              <p className="text-xs text-slate-600">AI-powered Anthropology feedback</p>
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          {(data.strongest_areas?.length > 0 || data.weakest_areas?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4">
              {data.strongest_areas?.length > 0 && (
                <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <p className="text-sm font-bold text-green-900">Strongest Areas</p>
                    </div>
                    <ul className="space-y-1.5">
                      {data.strongest_areas.map((area, idx) => (
                        <li key={idx} className="text-xs text-green-800 flex items-start gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {data.weakest_areas?.length > 0 && (
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-bold text-amber-900">Weakest Areas</p>
                    </div>
                    <ul className="space-y-1.5">
                      {data.weakest_areas.map((area, idx) => (
                        <li key={idx} className="text-xs text-amber-800 flex items-start gap-2">
                          <span className="text-amber-600">⚠</span>
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Structure Feedback */}
          {data.structure_feedback?.length > 0 && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <p className="text-sm font-bold text-blue-900">Structure & Flow</p>
                </div>
                <ul className="space-y-1.5">
                  {data.structure_feedback.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <CircleCheck className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{p.comment || p}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Depth Feedback */}
          {data.depth_feedback?.length > 0 && (
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-600" />
                  <p className="text-sm font-bold text-emerald-900">Depth & Coverage</p>
                </div>
                <ul className="space-y-1.5">
                  {data.depth_feedback.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{p.comment || p}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Keywords */}
          {data.keyword_suggestions?.length > 0 && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-purple-600" />
                  <p className="text-sm font-bold text-purple-900">Keywords to Add</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.keyword_suggestions.map((keyword, i) => (
                    <Badge key={i} className="bg-purple-100 text-purple-900 text-xs border-purple-300">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Missing Examples */}
          {data.missing_examples?.length > 0 && (
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <p className="text-sm font-bold text-amber-900">Missing Data & Examples</p>
                </div>
                <ul className="space-y-1.5">
                  {data.missing_examples.map((example, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <span className="text-amber-600 flex-shrink-0">•</span>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Missing Elements (Detailed) */}
          {data.missing_elements && (data.missing_elements.keywords?.length > 0 || data.missing_elements.data?.length > 0 || data.missing_elements.examples?.length > 0) && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-slate-600" />
                  <p className="text-sm font-bold text-slate-900">🔍 Missing Elements</p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {data.missing_elements.keywords?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Keywords</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.missing_elements.keywords.map((kw, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{kw}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.missing_elements.data?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Data/Statistics</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        {data.missing_elements.data.map((d, idx) => (
                          <li key={idx}>• {d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.missing_elements.examples?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Examples</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        {data.missing_elements.examples.map((ex, idx) => (
                          <li key={idx}>• {ex}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 10-Point Improvement Plan */}
          {data.improvement_plan?.length > 0 && (
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-600" />
                  <p className="text-sm font-bold text-indigo-900">🎯 10-Point Improvement Plan</p>
                </div>
                <ol className="space-y-2">
                  {data.improvement_plan.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                      <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                      </span>
                      <span>{typeof point === 'string' ? point.replace(/^\d+\.\s*/, '') : point}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* Rewrite Suggestions */}
          {data.rewrite_suggestions && (data.rewrite_suggestions.introduction || data.rewrite_suggestions.key_paragraph) && (
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-purple-600" />
                  <p className="text-sm font-bold text-purple-900">✏️ Rewrite Suggestions</p>
                </div>
                {data.rewrite_suggestions.introduction && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-purple-700">Improved Introduction</p>
                    <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                      {data.rewrite_suggestions.introduction}
                    </p>
                  </div>
                )}
                {data.rewrite_suggestions.key_paragraph && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-purple-700">Rewritten Key Paragraph</p>
                    <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-purple-200">
                      {data.rewrite_suggestions.key_paragraph}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Better Intro */}
          {data.better_intro && (
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <p className="text-sm font-bold text-indigo-900">Suggested Better Introduction</p>
                </div>
                <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                  {data.better_intro}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Better Conclusion */}
          {data.better_conclusion && (
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <p className="text-sm font-bold text-indigo-900">Suggested Better Conclusion</p>
                </div>
                <p className="text-xs text-slate-700 bg-white rounded-lg p-3 border border-indigo-200">
                  {data.better_conclusion}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Meta Tips */}
          {data.meta_tips?.length > 0 && (
            <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-rose-600" />
                  <p className="text-sm font-bold text-rose-900">Meta Tips for Improvement</p>
                </div>
                <ul className="space-y-1.5">
                  {data.meta_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                      <span className="text-rose-600 flex-shrink-0 font-bold">→</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Button
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/optional/anthropology')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Anthropology Suite
          </Button>
          
          <div className="flex items-center gap-4">
            <span className="text-5xl">🧠</span>
            <div>
              <h1 className="text-4xl font-bold">Paper II</h1>
              <p className="text-indigo-100">Indian Anthropology</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4">
            {stats && (
              <>
                <Badge variant="outline" className="text-white border-white/30">
                  {stats.answers_generated || 0} Answers
                </Badge>
                <Badge variant="outline" className="text-white border-white/30">
                  {stats.evaluations_done || 0} Evaluations
                </Badge>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Answer Generation */}
          <TabsContent value="generate" className="space-y-4">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-xl text-slate-900 mb-1 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-indigo-600" />
                    Answer Writing
                  </h3>
                  <p className="text-slate-500">TOP RANKER MODE – Write like a 300+ scorer with visual diagrams</p>
                </div>
                
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="md:col-span-3">
                    <Label>Question</Label>
                    <Textarea
                      placeholder="Enter your Anthropology Paper-II question..."
                      value={answerForm.question}
                      onChange={(e) => setAnswerForm({ ...answerForm, question: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Marks</Label>
                    <select
                      value={answerForm.marks}
                      onChange={(e) => setAnswerForm({ ...answerForm, marks: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-md h-10"
                    >
                      <option value={10}>10 marks</option>
                      <option value={15}>15 marks</option>
                      <option value={20}>20 marks</option>
                    </select>
                  </div>
                </div>

                <Button 
                  onClick={generateAnswer} 
                  disabled={loading} 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-lg"
                >
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PenTool className="w-5 h-5 mr-2" />}
                  Generate TOP RANKER Answer
                </Button>
              </CardContent>
            </Card>

            {renderAnswer(generatedAnswer)}
          </TabsContent>

          {/* ENGINE-2: Evaluation */}
          <TabsContent value="evaluate" className="space-y-4">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-xl text-slate-900 mb-1 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Answer Evaluation
                  </h3>
                  <p className="text-slate-500 text-sm">Get AI feedback on your Anthropology answers</p>
                </div>

                {/* Input Mode Toggle */}
                <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg w-fit">
                  <Button
                    type="button"
                    variant={inputMode === 'type' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setInputMode('type')}
                    className={inputMode === 'type' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    <FileTextIcon className="w-4 h-4 mr-2" />
                    Type Answer
                  </Button>
                  <Button
                    type="button"
                    variant={inputMode === 'handwritten' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setInputMode('handwritten')}
                    className={inputMode === 'handwritten' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Upload Handwritten
                  </Button>
                </div>

                {inputMode === 'type' ? (
                  <>
                    <div>
                      <Label>Question</Label>
                      <Textarea
                        placeholder="Enter the question..."
                        value={evalForm.question}
                        onChange={(e) => setEvalForm({ ...evalForm, question: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                        <Label>Your Answer</Label>
                        <Textarea
                          placeholder="Paste your answer here..."
                          value={evalForm.answer}
                          onChange={(e) => setEvalForm({ ...evalForm, answer: e.target.value })}
                          rows={8}
                        />
                      </div>
                      <div>
                        <Label>Marks</Label>
                        <select
                          value={evalForm.marks}
                          onChange={(e) => setEvalForm({ ...evalForm, marks: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border rounded-md h-10"
                        >
                          <option value={10}>10 marks</option>
                          <option value={15}>15 marks</option>
                          <option value={20}>20 marks</option>
                        </select>
                      </div>
                    </div>

                    <Button 
                      onClick={evaluateAnswer} 
                      disabled={loading} 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
                      Evaluate Answer
                    </Button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Marks</Label>
                      <select
                        value={evalForm.marks}
                        onChange={(e) => setEvalForm({ ...evalForm, marks: parseInt(e.target.value) })}
                        className="px-3 py-2 border rounded-md"
                      >
                        <option value={10}>10 marks</option>
                        <option value={15}>15 marks</option>
                        <option value={20}>20 marks</option>
                      </select>
                    </div>
                    <HandwrittenUpload
                      marks={evalForm.marks}
                      suite="ANTHROPOLOGY_P2"
                      onEvaluationComplete={(data) => {
                        setEvaluation(data);
                        toast({ description: "Handwritten answer evaluated successfully!" });
                      }}
                      onExtractedData={(data) => {
                        setEvalForm(prev => ({
                          ...prev,
                          question: data.question || prev.question,
                          answer: data.answer || prev.answer
                        }));
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {renderEvaluation(evaluation)}
          </TabsContent>

          {/* ENGINE-3: PYQ Analysis */}
          <TabsContent value="pyq" className="space-y-4">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-bold text-xl text-slate-900 mb-1 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                    PYQ Analysis
                  </h3>
                  <p className="text-slate-500">30 YEARS – Trends, frequency, gap analysis, insights</p>
                </div>
                
                <div className="flex gap-4">
                  <input
                    type="text"
                    placeholder="Enter topic (e.g., Land Alienation, PESA, Tribal Rights)"
                    value={pyqTopic}
                    onChange={(e) => setPyqTopic(e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-md"
                  />
                  <Button onClick={analyzePYQ} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                    Analyze PYQ
                  </Button>
                </div>
              </CardContent>
            </Card>

            {pyqAnalysis && (
              <div className="space-y-4">
                {pyqAnalysis.preview ? (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-6 text-center">
                      <Lock className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                      <p className="text-amber-800">{pyqAnalysis.upgrade_message}</p>
                      <Button onClick={() => navigate('/optional')} className="mt-4 bg-amber-600">
                        <Crown className="w-4 h-4 mr-2" /> Upgrade
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Header with Topic and Badges */}
                    <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-red-50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <h4 className="text-xl font-bold text-slate-900">{pyqAnalysis.topic}</h4>
                            <Badge className={`${
                              pyqAnalysis.trend_tag?.includes('HOT') ? 'bg-red-600' :
                              pyqAnalysis.trend_tag?.includes('WARM') ? 'bg-orange-600' : 'bg-blue-600'
                            } text-white`}>
                              {pyqAnalysis.trend_tag?.includes('HOT') && <Flame className="w-3 h-3 mr-1" />}
                              {pyqAnalysis.trend_tag?.includes('WARM') && <Thermometer className="w-3 h-3 mr-1" />}
                              {pyqAnalysis.trend_tag?.includes('COLD') && <Snowflake className="w-3 h-3 mr-1" />}
                              {pyqAnalysis.trend_tag}
                            </Badge>
                            {pyqAnalysis.risk_tag && (
                              <Badge variant="outline" className="text-xs">
                                {pyqAnalysis.risk_tag}
                              </Badge>
                            )}
                          </div>
                          {pyqAnalysis.probability_next_exam && (
                            <Badge className="bg-purple-600 text-white">
                              Next Exam: {pyqAnalysis.probability_next_exam}
                            </Badge>
                          )}
                        </div>
                        {pyqAnalysis.syllabus_unit && (
                          <p className="text-sm text-slate-600 mt-2">📚 Syllabus: {pyqAnalysis.syllabus_unit}</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Summary Stats */}
                    {pyqAnalysis.summary && (
                      <div className="grid gap-4 md:grid-cols-4">
                        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                          <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-blue-900">{pyqAnalysis.summary.total_questions || 0}</p>
                            <p className="text-xs text-blue-700 mt-1">Total Questions</p>
                          </CardContent>
                        </Card>
                        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                          <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-purple-900">{pyqAnalysis.summary.avg_per_year || 0}</p>
                            <p className="text-xs text-purple-700 mt-1">Avg Questions/Year</p>
                          </CardContent>
                        </Card>
                        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                          <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-900">{pyqAnalysis.summary.last_appeared || "N/A"}</p>
                            <p className="text-xs text-emerald-700 mt-1">Last Appeared</p>
                          </CardContent>
                        </Card>
                        <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                          <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-rose-900">{pyqAnalysis.summary.trend || "↗️"}</p>
                            <p className="text-xs text-rose-700 mt-1">Trend Direction</p>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Topic Frequency Analysis */}
                    {pyqAnalysis.topic_frequency && pyqAnalysis.topic_frequency.length > 0 && (
                      <Card className="border-slate-200 bg-white">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-orange-600" />
                            Sub-Topic Frequency Analysis
                          </h3>
                          <div className="space-y-3">
                            {pyqAnalysis.topic_frequency.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 text-orange-900 rounded-full flex items-center justify-center text-xs font-bold">
                                  {idx + 1}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold text-slate-900">{item.topic}</span>
                                    <span className="text-sm font-bold text-orange-600">{item.count} Qs</span>
                                  </div>
                                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                                      style={{ width: `${(item.count / (pyqAnalysis.summary?.total_questions || 10)) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Year-wise Trends */}
                    {pyqAnalysis.year_wise && pyqAnalysis.year_wise.length > 0 && (
                      <Card className="border-slate-200 bg-white">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-orange-600" />
                            Year-wise Question Trends
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                            {pyqAnalysis.year_wise.map((item, idx) => (
                              <div key={idx} className={`p-3 rounded-lg text-center ${
                                item.count > 0 ? 'bg-orange-100 border border-orange-200' : 'bg-slate-50'
                              }`}>
                                <p className="text-sm font-bold text-slate-900">{item.year}</p>
                                <p className={`text-lg font-bold ${item.count > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                                  {item.count} Q{item.count !== 1 ? 's' : ''}
                                </p>
                                {item.marks && item.marks.length > 0 && (
                                  <p className="text-xs text-slate-500">{item.marks.join(', ')} marks</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Marks Distribution */}
                    {pyqAnalysis.marks_distribution && (
                      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2">
                            <Target className="w-4 h-4 text-indigo-600" />
                            Marks Distribution Pattern
                          </h3>
                          <div className="grid grid-cols-4 gap-3">
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-indigo-600">
                                {typeof pyqAnalysis.marks_distribution['10_markers'] === 'object' 
                                  ? pyqAnalysis.marks_distribution['10_markers']?.count || 0
                                  : pyqAnalysis.marks_distribution['10_markers'] || 0}
                              </p>
                              <p className="text-xs text-slate-500">10 markers</p>
                              {typeof pyqAnalysis.marks_distribution['10_markers'] === 'object' && pyqAnalysis.marks_distribution['10_markers']?.percentage && (
                                <p className="text-xs text-indigo-400">{pyqAnalysis.marks_distribution['10_markers'].percentage}</p>
                              )}
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-indigo-600">
                                {typeof pyqAnalysis.marks_distribution['15_markers'] === 'object' 
                                  ? pyqAnalysis.marks_distribution['15_markers']?.count || 0
                                  : pyqAnalysis.marks_distribution['15_markers'] || 0}
                              </p>
                              <p className="text-xs text-slate-500">15 markers</p>
                              {typeof pyqAnalysis.marks_distribution['15_markers'] === 'object' && pyqAnalysis.marks_distribution['15_markers']?.percentage && (
                                <p className="text-xs text-indigo-400">{pyqAnalysis.marks_distribution['15_markers'].percentage}</p>
                              )}
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-indigo-600">
                                {typeof pyqAnalysis.marks_distribution['20_markers'] === 'object' 
                                  ? pyqAnalysis.marks_distribution['20_markers']?.count || 0
                                  : pyqAnalysis.marks_distribution['20_markers'] || 0}
                              </p>
                              <p className="text-xs text-slate-500">20 markers</p>
                              {typeof pyqAnalysis.marks_distribution['20_markers'] === 'object' && pyqAnalysis.marks_distribution['20_markers']?.percentage && (
                                <p className="text-xs text-indigo-400">{pyqAnalysis.marks_distribution['20_markers'].percentage}</p>
                              )}
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg border-2 border-indigo-300">
                              <p className="text-sm font-bold text-indigo-600">{pyqAnalysis.marks_distribution.preferred_format || 'N/A'}</p>
                              <p className="text-xs text-slate-500">Preferred</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Thinker Frequency */}
                    {pyqAnalysis.thinker_frequency && pyqAnalysis.thinker_frequency.length > 0 && (
                      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-purple-900 mb-3 flex items-center gap-2">
                            <Brain className="w-4 h-4 text-purple-600" />
                            Key Thinkers Frequency
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {pyqAnalysis.thinker_frequency.map((item, idx) => (
                              <Badge key={idx} className="bg-purple-100 text-purple-900 border-purple-300 text-xs">
                                {item.thinker} ({item.mentions})
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Tribe-wise Focus (Paper-II Specific) */}
                    {pyqAnalysis.tribe_wise_focus && pyqAnalysis.tribe_wise_focus.length > 0 && (
                      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-teal-900 mb-3 flex items-center gap-2">
                            🏛️ Tribe-wise PYQ Focus
                          </h3>
                          <div className="space-y-3">
                            {pyqAnalysis.tribe_wise_focus.map((tribe, idx) => (
                              <div key={idx} className="bg-white p-3 rounded-lg border border-teal-200">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <span className="font-semibold text-teal-900">{tribe.tribe}</span>
                                    <span className="text-xs text-slate-500 ml-2">({tribe.region})</span>
                                  </div>
                                  <Badge className="bg-teal-100 text-teal-800 text-xs">
                                    {tribe.mentions} mentions
                                  </Badge>
                                </div>
                                {tribe.contexts && tribe.contexts.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {tribe.contexts.map((ctx, cIdx) => (
                                      <span key={cIdx} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">
                                        {ctx}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {tribe.last_asked && (
                                  <p className="text-xs text-slate-500 mt-1">Last asked: {tribe.last_asked}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Policy/Constitutional Focus (Paper-II Specific) */}
                    {pyqAnalysis.policy_constitutional_focus && pyqAnalysis.policy_constitutional_focus.length > 0 && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                            📜 Policy & Constitutional Focus
                          </h3>
                          <div className="space-y-3">
                            {pyqAnalysis.policy_constitutional_focus.map((policy, idx) => (
                              <div key={idx} className="bg-white p-3 rounded-lg border border-amber-200">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-semibold text-amber-900">{policy.policy}</span>
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">
                                    {policy.frequency}x asked
                                  </Badge>
                                </div>
                                {policy.common_angles && policy.common_angles.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {policy.common_angles.map((angle, aIdx) => (
                                      <span key={aIdx} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">
                                        {angle}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {policy.last_asked && (
                                  <p className="text-xs text-slate-500 mt-1">Last asked: {policy.last_asked}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Actual PYQs */}
                    {pyqAnalysis.questions && pyqAnalysis.questions.length > 0 && (
                      <Card className="border-slate-200 bg-white">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-orange-600" />
                            Previous Year Questions ({pyqAnalysis.questions.length})
                          </h3>
                          <div className="space-y-3">
                            {pyqAnalysis.questions.map((q, idx) => (
                              <Card key={idx} className="border-orange-200 bg-orange-50/50 hover:shadow-md transition-shadow">
                                <CardContent className="p-4">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <Badge variant="outline" className="bg-orange-100 text-orange-900 border-orange-300">
                                        {q.year}
                                      </Badge>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm text-slate-800 font-medium mb-2">{q.question}</p>
                                      <div className="flex gap-2">
                                        {q.marks && (
                                          <Badge variant="secondary" className="text-xs">{q.marks} marks</Badge>
                                        )}
                                        {q.topic && (
                                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-900">
                                            {q.topic}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setAnswerForm({ ...answerForm, question: q.question, marks: q.marks || 15 });
                                        setActiveTab('generate');
                                      }}
                                      className="text-xs"
                                    >
                                      Practice
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Gap Analysis */}
                    {pyqAnalysis.gap_analysis && (
                      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-600" />
                            Gap Analysis
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-blue-600">{pyqAnalysis.gap_analysis.years_since_last || 0}</p>
                              <p className="text-xs text-slate-500">Years Since Last</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-blue-600">{pyqAnalysis.gap_analysis.longest_gap || 0}</p>
                              <p className="text-xs text-slate-500">Longest Gap</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg">
                              <p className="text-xl font-bold text-blue-600">{pyqAnalysis.gap_analysis.average_gap || 0}</p>
                              <p className="text-xs text-slate-500">Average Gap</p>
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg col-span-2 md:col-span-1">
                              <p className="text-xs font-bold text-blue-600">{pyqAnalysis.gap_analysis.due_status || 'N/A'}</p>
                              <p className="text-xs text-slate-500">Status</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Related Topics */}
                    {pyqAnalysis.related_topics && pyqAnalysis.related_topics.length > 0 && (
                      <Card className="border-slate-200 bg-white">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                            🔗 Related Topics
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {pyqAnalysis.related_topics.map((item, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className={`text-xs cursor-pointer hover:bg-slate-100 ${
                                  item.overlap === 'High' ? 'border-green-400 text-green-700' :
                                  item.overlap === 'Medium' ? 'border-amber-400 text-amber-700' : 'border-slate-300'
                                }`}
                                onClick={() => setPyqTopic(item.topic)}
                              >
                                {item.topic} ({item.overlap})
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Exam Strategy */}
                    {pyqAnalysis.exam_strategy && (
                      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-green-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Exam Strategy
                          </h3>
                          <div className="space-y-3">
                            {pyqAnalysis.exam_strategy.must_know_thinkers && (
                              <div>
                                <p className="text-xs font-semibold text-green-800 mb-1">Must-Know Thinkers:</p>
                                <div className="flex flex-wrap gap-1">
                                  {pyqAnalysis.exam_strategy.must_know_thinkers.map((t, i) => (
                                    <Badge key={i} className="bg-green-100 text-green-800 text-xs">{t}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {pyqAnalysis.exam_strategy.must_know_concepts && (
                              <div>
                                <p className="text-xs font-semibold text-green-800 mb-1">Must-Know Concepts:</p>
                                <div className="flex flex-wrap gap-1">
                                  {pyqAnalysis.exam_strategy.must_know_concepts.map((c, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {pyqAnalysis.exam_strategy.diagram_suggestion && (
                              <div className="bg-white p-2 rounded-lg">
                                <p className="text-xs"><strong>📊 Diagram:</strong> {pyqAnalysis.exam_strategy.diagram_suggestion}</p>
                              </div>
                            )}
                            {pyqAnalysis.exam_strategy.word_limit_tip && (
                              <div className="bg-white p-2 rounded-lg">
                                <p className="text-xs"><strong>📝 Tip:</strong> {pyqAnalysis.exam_strategy.word_limit_tip}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Insights */}
                    {pyqAnalysis.insights && pyqAnalysis.insights.length > 0 && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-amber-600" />
                            Key Insights & Forecasting
                          </h3>
                          <ul className="space-y-2">
                            {pyqAnalysis.insights.map((insight, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-800">
                                <span className="text-amber-600 flex-shrink-0 mt-0.5">💡</span>
                                <span>{insight}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* Focus Areas */}
                    {pyqAnalysis.focus_areas && pyqAnalysis.focus_areas.length > 0 && (
                      <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                        <CardContent className="p-4">
                          <h3 className="text-sm font-bold text-rose-900 mb-3 flex items-center gap-2">
                            🎯 Focus Areas for Preparation
                          </h3>
                          <ol className="space-y-2">
                            {pyqAnalysis.focus_areas.map((area, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-800">
                                <span className="flex-shrink-0 w-5 h-5 bg-rose-100 text-rose-700 rounded-full flex items-center justify-center text-xs font-bold">
                                  {idx + 1}
                                </span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ol>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Expected Questions */}
          <TabsContent value="expected" className="space-y-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-xl text-slate-900 mb-1 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      Expected Questions
                    </h3>
                    <p className="text-slate-500">PREDICTION – 30-year PYQ rotation + Gap analysis + Heat mapping</p>
                  </div>
                  <Button onClick={loadExpectedQuestions} disabled={loading} className="bg-green-600 hover:bg-green-700">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : '🔮 Generate Predictions'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {expectedQuestions && (
              <div className="space-y-6">
                {/* FREE USER PREVIEW */}
                {expectedQuestions.preview ? (
                  <div className="space-y-4">
                    {/* Preview Cards */}
                    <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <Lock className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                          <h4 className="font-bold text-lg text-amber-900">Full Prediction Engine Locked</h4>
                          <p className="text-amber-700">{expectedQuestions.upgrade_message}</p>
                        </div>
                        
                        {/* Sample Predictions Preview */}
                        {expectedQuestions.preview_questions && (
                          <div className="mb-6">
                            <h5 className="font-semibold text-slate-700 mb-3">🔥 Sample Hot Predictions:</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {expectedQuestions.preview_questions.map((pred, idx) => (
                                <div key={idx} className="bg-white/80 p-3 rounded-lg border border-amber-200">
                                  <div className="flex justify-between items-start">
                                    <span className="font-medium text-slate-800">{pred.topic}</span>
                                    <Badge className="bg-amber-100 text-amber-800 text-xs">{pred.marks}M</Badge>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">{pred.hint}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Locked Features List */}
                        <div className="mb-6">
                          <h5 className="font-semibold text-slate-700 mb-2">🔒 PRO Features Include:</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {['Unit-wise Heat Forecast', 'Topic Heat Map', 'Tribe Rotation Forecast', 'Full PYQ History', 
                              'Preparation Tips', 'Key Terms & Diagrams', 'Cross-linked Topics', 'Examiner Patterns'].map((feat, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                                <span className="text-amber-500">✦</span> {feat}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <Button onClick={() => navigate('/optional')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                            <Crown className="w-4 h-4 mr-2" /> Unlock Full Predictions
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  /* PRO USER - FULL ENHANCED CONTENT */
                  <div className="space-y-6">
                    {/* Prediction Confidence Banner */}
                    {expectedQuestions.prediction_confidence && (
                      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="font-semibold text-blue-900">📊 Prediction Methodology</h5>
                              <p className="text-sm text-blue-700">{expectedQuestions.prediction_confidence.methodology}</p>
                            </div>
                            <Badge className="bg-blue-100 text-blue-800">{expectedQuestions.cycle || '2025-26'}</Badge>
                          </div>
                          {expectedQuestions.prediction_confidence.disclaimer && (
                            <p className="text-xs text-blue-600 mt-2 italic">⚠️ {expectedQuestions.prediction_confidence.disclaimer}</p>
                          )}
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Unit-wise Forecast Heat Map */}
                    {expectedQuestions.unit_wise_forecast && (
                      <Card>
                        <CardContent className="p-4">
                          <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                            📈 Unit-wise Heat Forecast
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {expectedQuestions.unit_wise_forecast.map((unit, idx) => (
                              <div key={idx} className={`p-3 rounded-lg border ${
                                unit.heat_level?.includes('VERY HOT') ? 'bg-red-50 border-red-200' :
                                unit.heat_level?.includes('HOT') ? 'bg-orange-50 border-orange-200' :
                                unit.heat_level?.includes('WARM') ? 'bg-yellow-50 border-yellow-200' :
                                'bg-blue-50 border-blue-200'
                              }`}>
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-medium text-sm">{unit.unit}</span>
                                  <span className="text-lg">{unit.heat_level}</span>
                                </div>
                                <div className="text-xs text-slate-600">
                                  <span className="font-medium">Expected: {unit.expected_marks} marks</span>
                                  {unit.last_unit_appearance && (
                                    <span className="ml-2 text-slate-500">• Last: {unit.last_unit_appearance}</span>
                                  )}
                                </div>
                                {unit.probable_topics && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {unit.probable_topics.slice(0, 3).map((topic, tIdx) => (
                                      <span key={tIdx} className="text-xs bg-white/80 px-2 py-0.5 rounded">{topic}</span>
                                    ))}
                                  </div>
                                )}
                                {unit.tribe_focus && (
                                  <p className="text-xs text-slate-500 mt-1">🏛️ {unit.tribe_focus}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Topic Heat Map */}
                    {expectedQuestions.topic_heat_map && (
                      <Card>
                        <CardContent className="p-4">
                          <h4 className="font-bold text-lg mb-4">🔥 Topic Heat Map</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Very Hot */}
                            {expectedQuestions.topic_heat_map.very_hot && (
                              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                <h5 className="font-semibold text-red-800 mb-2">🔥🔥 Very Hot</h5>
                                {expectedQuestions.topic_heat_map.very_hot.map((t, i) => (
                                  <div key={i} className="text-sm mb-2 pb-2 border-b border-red-100 last:border-0">
                                    <span className="font-medium text-red-900">{t.topic}</span>
                                    <div className="flex justify-between text-xs text-red-600">
                                      <span>Gap: {t.gap}y</span>
                                      <span className="font-medium">{t.probability}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Hot */}
                            {expectedQuestions.topic_heat_map.hot && (
                              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                                <h5 className="font-semibold text-orange-800 mb-2">🔥 Hot</h5>
                                {expectedQuestions.topic_heat_map.hot.map((t, i) => (
                                  <div key={i} className="text-sm mb-2 pb-2 border-b border-orange-100 last:border-0">
                                    <span className="font-medium text-orange-900">{t.topic}</span>
                                    <div className="flex justify-between text-xs text-orange-600">
                                      <span>Gap: {t.gap}y</span>
                                      <span className="font-medium">{t.probability}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Warm */}
                            {expectedQuestions.topic_heat_map.warm && (
                              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                <h5 className="font-semibold text-yellow-800 mb-2">🟡 Warm</h5>
                                {expectedQuestions.topic_heat_map.warm.map((t, i) => (
                                  <div key={i} className="text-sm mb-2 pb-2 border-b border-yellow-100 last:border-0">
                                    <span className="font-medium text-yellow-900">{t.topic}</span>
                                    <div className="flex justify-between text-xs text-yellow-600">
                                      <span>Gap: {t.gap}y</span>
                                      <span className="font-medium">{t.probability}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Cold */}
                            {expectedQuestions.topic_heat_map.cold && (
                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <h5 className="font-semibold text-blue-800 mb-2">❄️ Cold</h5>
                                {expectedQuestions.topic_heat_map.cold.map((t, i) => (
                                  <div key={i} className="text-sm mb-2 pb-2 border-b border-blue-100 last:border-0">
                                    <span className="font-medium text-blue-900">{t.topic}</span>
                                    <div className="flex justify-between text-xs text-blue-600">
                                      <span>Gap: {t.gap}y</span>
                                      <span className="font-medium">{t.probability}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Tribe Rotation Forecast (Paper-II Specific) */}
                    {expectedQuestions.tribe_rotation_forecast && expectedQuestions.tribe_rotation_forecast.length > 0 && (
                      <Card className="border-teal-200">
                        <CardContent className="p-4">
                          <h4 className="font-bold text-lg mb-4">🏛️ Tribe Rotation Forecast</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {expectedQuestions.tribe_rotation_forecast.map((tribe, idx) => (
                              <div key={idx} className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <span className="font-semibold text-teal-900">{tribe.tribe}</span>
                                    <span className="text-xs text-slate-500 ml-1">({tribe.region})</span>
                                  </div>
                                  <Badge className={`text-xs ${
                                    tribe.probability?.includes('85') || tribe.probability?.includes('HIGH') ? 'bg-red-100 text-red-800' :
                                    tribe.probability?.includes('70') || tribe.probability?.includes('75') ? 'bg-orange-100 text-orange-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>{tribe.probability}</Badge>
                                </div>
                                <p className="text-xs text-teal-700">{tribe.likely_context}</p>
                                {tribe.last_asked && (
                                  <p className="text-xs text-slate-500 mt-1">Last asked: {tribe.last_asked}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Expected Questions by Section */}
                    {expectedQuestions.expected_questions && (
                      <div className="space-y-4">
                        {['section_a_20_markers', 'section_a_15_markers', 'section_b_15_markers', 'section_b_10_markers', '20_markers', '15_markers', '10_markers'].map((category) => (
                          expectedQuestions.expected_questions[category] && expectedQuestions.expected_questions[category].length > 0 && (
                            <Card key={category}>
                              <CardContent className="p-4">
                                <h4 className="font-bold text-lg mb-4 capitalize flex items-center gap-2">
                                  {category.includes('20') ? '📝' : category.includes('15') ? '📋' : '📄'}
                                  {category.replace(/_/g, ' ').replace('section a ', 'Section A: ').replace('section b ', 'Section B: ').toUpperCase()}
                                </h4>
                                <div className="space-y-4">
                                  {expectedQuestions.expected_questions[category].map((q, idx) => (
                                    <div key={idx} className="bg-gradient-to-r from-slate-50 to-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-all">
                                      {/* Question Header */}
                                      <div className="flex justify-between items-start mb-3">
                                        <p className="font-semibold text-slate-900 flex-1 pr-4">{q.question}</p>
                                        <div className="flex flex-col items-end gap-1">
                                          <Badge className="bg-indigo-100 text-indigo-800">{q.marks || category.match(/\d+/)?.[0]}M</Badge>
                                          {q.probability && (
                                            <Badge className={`text-xs ${
                                              q.probability.includes('VERY HIGH') ? 'bg-red-100 text-red-800' :
                                              q.probability.includes('HIGH') ? 'bg-orange-100 text-orange-800' :
                                              'bg-yellow-100 text-yellow-800'
                                            }`}>{q.probability}</Badge>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Tags Row */}
                                      <div className="flex flex-wrap gap-2 mb-3">
                                        {q.syllabus_unit && (
                                          <Badge variant="outline" className="text-xs bg-blue-50">{q.syllabus_unit}</Badge>
                                        )}
                                        {q.sub_topic && (
                                          <Badge variant="outline" className="text-xs bg-purple-50">{q.sub_topic}</Badge>
                                        )}
                                        {q.word_limit && (
                                          <Badge variant="outline" className="text-xs bg-slate-100">{q.word_limit}</Badge>
                                        )}
                                      </div>
                                      
                                      {/* PYQ History */}
                                      {q.pyq_history && (
                                        <div className="bg-amber-50 p-3 rounded-lg mb-3 text-sm">
                                          <h6 className="font-semibold text-amber-900 mb-1">📚 PYQ History</h6>
                                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-amber-800">
                                            <div><span className="text-amber-600">Last Asked:</span> {q.pyq_history.last_asked}</div>
                                            <div><span className="text-amber-600">Gap:</span> {q.pyq_history.gap_years} years</div>
                                            <div><span className="text-amber-600">Frequency:</span> {q.pyq_history.frequency}</div>
                                            {q.pyq_history.years && (
                                              <div><span className="text-amber-600">Years:</span> {q.pyq_history.years.slice(0, 4).join(', ')}</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Why Expected */}
                                      {(q.why_expected || q.why_now) && (
                                        <div className="bg-green-50 p-3 rounded-lg mb-3">
                                          <p className="text-sm text-green-800">
                                            <span className="font-semibold">💡 Why Expected:</span> {q.why_expected || q.why_now}
                                          </p>
                                        </div>
                                      )}
                                      
                                      {/* Preparation Tips */}
                                      {q.preparation_tips && q.preparation_tips.length > 0 && (
                                        <div className="bg-blue-50 p-3 rounded-lg mb-3">
                                          <h6 className="font-semibold text-blue-900 mb-2">🎯 Preparation Tips</h6>
                                          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                                            {q.preparation_tips.map((tip, tIdx) => (
                                              <li key={tIdx}>{tip}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      
                                      {/* Tribe Examples (Paper-II Specific) */}
                                      {q.tribe_examples && q.tribe_examples.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                          <span className="text-xs font-medium text-teal-700">🏛️ Tribes:</span>
                                          {q.tribe_examples.map((tribe, tIdx) => (
                                            <span key={tIdx} className="inline-block bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs">{tribe}</span>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Key Terms & Diagram */}
                                      <div className="flex flex-wrap gap-4 text-sm">
                                        {q.key_terms && q.key_terms.length > 0 && (
                                          <div>
                                            <span className="font-medium text-slate-700">Key Terms: </span>
                                            {q.key_terms.map((term, kIdx) => (
                                              <span key={kIdx} className="inline-block bg-slate-100 px-2 py-0.5 rounded text-xs mr-1 mb-1">{term}</span>
                                            ))}
                                          </div>
                                        )}
                                        {q.diagram_suggestion && (
                                          <div className="text-purple-700">
                                            <span className="font-medium">📊 Diagram:</span> {q.diagram_suggestion}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Cross Links */}
                                      {q.cross_links && q.cross_links.length > 0 && (
                                        <div className="mt-2 text-xs text-slate-500">
                                          <span className="font-medium">🔗 Related:</span> {q.cross_links.join(' • ')}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )
                        ))}
                      </div>
                    )}
                    
                    {/* Preparation Priority (Paper-II Specific) */}
                    {expectedQuestions.preparation_priority && (
                      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                        <CardContent className="p-4">
                          <h4 className="font-bold text-lg mb-4 text-emerald-900">📚 Preparation Priority</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {expectedQuestions.preparation_priority.priority_level && (
                              <div className="bg-white p-3 rounded-lg">
                                <h5 className="font-semibold text-emerald-800 mb-1 text-sm">Priority Level</h5>
                                <Badge className="bg-emerald-100 text-emerald-800">{expectedQuestions.preparation_priority.priority_level}</Badge>
                              </div>
                            )}
                            {expectedQuestions.preparation_priority.time_investment && (
                              <div className="bg-white p-3 rounded-lg">
                                <h5 className="font-semibold text-emerald-800 mb-1 text-sm">Time Investment</h5>
                                <p className="text-sm text-emerald-700">{expectedQuestions.preparation_priority.time_investment}</p>
                              </div>
                            )}
                            {expectedQuestions.preparation_priority.must_know_tribes && (
                              <div className="bg-white p-3 rounded-lg">
                                <h5 className="font-semibold text-emerald-800 mb-2 text-sm">Must-Know Tribes</h5>
                                <div className="flex flex-wrap gap-1">
                                  {expectedQuestions.preparation_priority.must_know_tribes.map((tribe, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{tribe}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {expectedQuestions.preparation_priority.must_know_policies && (
                              <div className="bg-white p-3 rounded-lg">
                                <h5 className="font-semibold text-emerald-800 mb-2 text-sm">Must-Know Policies</h5>
                                <div className="flex flex-wrap gap-1">
                                  {expectedQuestions.preparation_priority.must_know_policies.map((policy, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{policy}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {expectedQuestions.preparation_priority.must_know_cases && (
                            <div className="mt-4 bg-white p-3 rounded-lg">
                              <h5 className="font-semibold text-emerald-800 mb-2 text-sm">Must-Know Case Studies</h5>
                              <div className="flex flex-wrap gap-2">
                                {expectedQuestions.preparation_priority.must_know_cases.map((caseStudy, i) => (
                                  <Badge key={i} className="bg-amber-100 text-amber-800 text-xs">{caseStudy}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Forecast */}
                    {expectedQuestions.forecast && (
                      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                        <CardContent className="p-4">
                          <h4 className="font-bold text-lg mb-4 text-purple-900">🔮 2025-26 Forecast</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {expectedQuestions.forecast.next_exam_probability && (
                              <div className="bg-white p-3 rounded-lg text-center">
                                <p className="text-2xl font-bold text-purple-600">{expectedQuestions.forecast.next_exam_probability}</p>
                                <p className="text-xs text-slate-500">Exam Probability</p>
                              </div>
                            )}
                            {expectedQuestions.forecast.likely_marks && (
                              <div className="bg-white p-3 rounded-lg text-center">
                                <p className="text-lg font-bold text-purple-600">{expectedQuestions.forecast.likely_marks}</p>
                                <p className="text-xs text-slate-500">Likely Marks</p>
                              </div>
                            )}
                            {expectedQuestions.forecast.likely_angle && (
                              <div className="bg-white p-3 rounded-lg col-span-2">
                                <p className="text-xs font-semibold text-purple-700 mb-1">Likely Angle</p>
                                <p className="text-sm text-slate-700">{expectedQuestions.forecast.likely_angle}</p>
                              </div>
                            )}
                          </div>
                          {expectedQuestions.forecast.wildcard_angles && expectedQuestions.forecast.wildcard_angles.length > 0 && (
                            <div className="mt-3 bg-white p-3 rounded-lg">
                              <p className="text-xs font-semibold text-purple-700 mb-2">🃏 Wildcard Angles</p>
                              <div className="flex flex-wrap gap-2">
                                {expectedQuestions.forecast.wildcard_angles.map((angle, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{angle}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Paper2Page;
